import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionService, UserService } from '@socrates/socrates-shared';

@Component({
	selector: 'app-sidebar',
	templateUrl: './appSidebar.component.html',
	standalone: false,
	host: { style: 'display: flex; flex-direction: column; flex: 1; min-height: 0; overflow: hidden;' },
})
export class AppSidebarComponent {
	@Input() collapsed = false;
	@Output() collapsedChange = new EventEmitter<boolean>();

	// Beveled edge: light top-left, dark bottom-right — like a routed/chamfered physical button
	private readonly _bevelRest   = 'inset 2px 2px 3px rgba(255,255,255,0.2), inset -2px -2px 3px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.6)';
	private readonly _bevelHover  = 'inset 2px 2px 3px rgba(255,255,255,0.28), inset -2px -2px 3px rgba(0,0,0,0.55), 0 6px 14px rgba(0,0,0,0.65)';
	private readonly _bevelActive = 'inset 2px 2px 4px rgba(156,130,80,0.35), inset -2px -2px 4px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.5)';
	private readonly _bevelPressed= 'inset 3px 3px 5px rgba(0,0,0,0.5), inset -1px -1px 2px rgba(255,255,255,0.08)';

	private readonly destroyRef = inject(DestroyRef);

	constructor(
		private router: Router,
		private permissionService: PermissionService,
		private userService: UserService,
	) {
		// Keep router subscription for future use (e.g. active state refresh)
		this.router.events.pipe(
			filter(e => e instanceof NavigationEnd),
			takeUntilDestroyed(this.destroyRef)
		).subscribe();
	}

	get bevelRest(): string { return this._bevelRest; }

	toggle(): void {
		this.collapsed = !this.collapsed;
		this.collapsedChange.emit(this.collapsed);
	}

	navigate(path: string): void {
		this.router.navigateByUrl(path);
	}

	isActive(path: string): boolean {
		return this.router.url.startsWith(path);
	}

	buttonShadow(path: string): string {
		return this.isActive(path) ? this._bevelActive : this._bevelRest;
	}

	adminButtonBg(path: string): string {
		return this.isActive(path) ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.75)';
	}

	laurelOpacity(path: string): string {
		return this.isActive(path) ? '0.14' : '0.06';
	}

	iconStroke(path: string): string {
		return this.isActive(path) ? '#c4a255' : '#9c8250';
	}

	onButtonEnter(event: MouseEvent, path: string): void {
		const btn = event.currentTarget as HTMLElement;
		btn.style.transform = 'translateY(-2px)';
		btn.style.boxShadow = this._bevelHover;
	}

	onButtonLeave(event: MouseEvent, path: string, admin = false): void {
		const btn = event.currentTarget as HTMLElement;
		btn.style.transform = '';
		btn.style.backgroundColor = admin ? this.adminButtonBg(path) : (this.isActive(path) ? '#3a6090' : '#2a4a68');
		btn.style.boxShadow = this.buttonShadow(path);
		const laurel = btn.querySelector('.laurel-inner') as HTMLElement | null;
		if (laurel) { laurel.style.opacity = this.laurelOpacity(path); laurel.style.filter = ''; }
	}

	onButtonDown(event: MouseEvent): void {
		const btn = event.currentTarget as HTMLElement;
		btn.style.transform = 'translateY(1px)';
		btn.style.boxShadow = this._bevelPressed;
		const laurel = btn.querySelector('.laurel-inner') as HTMLElement | null;
		if (laurel) { laurel.style.opacity = '0.6'; laurel.style.filter = 'sepia(1) hue-rotate(5deg) saturate(2)'; }
	}

	onButtonUp(event: MouseEvent): void {
		const btn = event.currentTarget as HTMLElement;
		btn.style.transform = '';
		btn.style.boxShadow = this._bevelHover;
		const laurel = btn.querySelector('.laurel-inner') as HTMLElement | null;
		if (laurel) { laurel.style.opacity = '0.1'; laurel.style.filter = ''; }
	}

	get isPillarReviewRequired(): boolean {
		return this.userService.getUser()?.isPillarReviewRequired ?? false;
	}

	get hasAnyAdminAccess(): boolean {
		const user = this.userService.user;
		if (!user) return false;
		return (
			this.permissionService.permitted(user, 'IdeaManagement.ADMIN') ||
			this.permissionService.permitted(user, 'UserManagement.READ') ||
			this.permissionService.permitted(user, 'ModerationManagement.READ') ||
			this.permissionService.permitted(user, 'ReportingManagement.READ') ||
			this.permissionService.permitted(user, 'ConfigManagement.READ')
		);
	}

	get canPillarsAdmin(): boolean {
		const user = this.userService.user;
		return !!user && this.permissionService.permitted(user, 'IdeaManagement.ADMIN');
	}

	get canConfig(): boolean {
		const user = this.userService.user;
		return !!user && this.permissionService.permitted(user, 'ConfigManagement.READ');
	}

	get canMembers(): boolean {
		const user = this.userService.user;
		return !!user && this.permissionService.permitted(user, 'UserManagement.READ');
	}

	get canIdeas(): boolean {
		const user = this.userService.user;
		return !!user && this.permissionService.permitted(user, 'IdeaManagement.ADMIN');
	}

	get canModeration(): boolean {
		const user = this.userService.user;
		return !!user && this.permissionService.permitted(user, 'ModerationManagement.READ');
	}

	get canReporting(): boolean {
		const user = this.userService.user;
		return !!user && this.permissionService.permitted(user, 'ReportingManagement.READ');
	}
}
